import React, { useState } from 'react';
import { useReveal } from '../lib/useReveal';
import { cn } from '../lib/cn';

const initialState = { name: '', email: '', message: '' };

export const Contact = ({ data, language = 'bg' }) => {
  const headRef = useReveal();
  const colRef = useReveal();
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Server error');
      }
      setSubmitStatus('success');
      setFormData(initialState);
    } catch (err) {
      console.error('Contact form error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addr = data?.address || 'Bukovo 2915 Bukovo, Bulgaria';
  const phone = data?.phone || '+359883485326';
  const email = data?.email || 'office@dv-plast.com';
  const facebook = data?.facebook || 'https://www.facebook.com/dv.dograma/';
  const mapsQuery = encodeURIComponent(addr);

  return (
    <section id="kontakti" className="section-pad bg-ink text-paper relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Cpath d='M0 20h40M20 0v40' stroke='%23F5F1EA' stroke-width='0.5'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="max-wrap section-wrap relative">
        <div ref={headRef} className="reveal grid grid-cols-12 gap-y-6 gap-x-6 items-end">
          <div className="col-span-12 lg:col-span-3">
            <span className="font-mono text-index text-paper/55">04 / 04</span>
            <span className="block mt-4 h-px w-16 bg-paper/50" aria-hidden="true" />
          </div>
          <div className="col-span-12 lg:col-span-9">
            <h2 className="font-serif text-balance tracking-tightest leading-[0.98]"
                style={{ fontSize: 'clamp(2.25rem, 8vw, 6rem)' }}>
              {data?.title || (language === 'bg' ? 'Свържете се с нас' : 'Contact Us')}
            </h2>
            {data?.desc && (
              <p className="mt-8 max-w-2xl text-paper/75 text-base sm:text-lg leading-relaxed text-pretty">
                {data.desc}
              </p>
            )}
          </div>
        </div>

        <div ref={colRef} className="reveal mt-16 sm:mt-24 grid grid-cols-12 gap-y-14 gap-x-6 md:gap-x-10 lg:gap-x-16">
          <div className="col-span-12 lg:col-span-5">
            <dl className="space-y-8 border-t border-paper/15">
              <ContactRow icon="location">
                <p className="font-serif text-xl sm:text-2xl leading-snug tracking-editorial text-paper">
                  {addr}
                </p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 font-mono text-[0.68rem] uppercase tracking-[0.22em] text-paper/60 hover:text-paper transition-colors"
                >
                  Google Maps
                  <svg viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5" aria-hidden="true">
                    <path stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" d="M7 17L17 7M17 7H9M17 7V15" />
                  </svg>
                </a>
              </ContactRow>

              <ContactRow icon="phone">
                <a
                  href={`tel:${phone.replace(/\s+/g, '')}`}
                  className="font-serif text-xl sm:text-2xl leading-snug tracking-editorial text-paper hover:text-brass-soft transition-colors"
                >
                  {phone}
                </a>
              </ContactRow>

              <ContactRow icon="mail">
                <a
                  href={`mailto:${email}`}
                  className="font-serif text-xl sm:text-2xl leading-snug tracking-editorial text-paper hover:text-brass-soft transition-colors break-all"
                >
                  {email}
                </a>
              </ContactRow>

              <ContactRow icon="social">
                <a
                  href={facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-xl sm:text-2xl leading-snug tracking-editorial text-paper hover:text-brass-soft transition-colors"
                  aria-label="Facebook"
                >
                  Facebook
                </a>
              </ContactRow>
            </dl>

            <a
              href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              className="relative block mt-12 h-52 sm:h-64 group overflow-hidden border border-paper/15"
              aria-label={addr}
            >
              <iframe
                src={`https://www.google.com/maps?q=${mapsQuery}&t=&z=11&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={addr}
                className="pointer-events-none"
                aria-hidden="true"
              />
            </a>
          </div>

          <form
            onSubmit={handleSubmit}
            className="col-span-12 lg:col-span-7 lg:pl-10"
            noValidate
          >
            <div className="space-y-8">
              <Field
                id="name"
                label={data?.inputs?.name || 'Name'}
                required
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
              />
              <Field
                id="email"
                type="email"
                label={data?.inputs?.email || 'Email'}
                required
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
              />
              <Field
                id="message"
                as="textarea"
                rows={5}
                label={data?.inputs?.message || 'Message'}
                required
                value={formData.message}
                onChange={handleChange}
              />
            </div>

            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="group inline-flex items-center gap-4 cursor-pointer text-paper disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="relative font-sans text-sm tracking-[0.18em] uppercase">
                  {isSubmitting ? 'Sending…' : (data?.button || 'Send')}
                  <span className="absolute left-0 -bottom-1 w-full h-px bg-paper transition-transform duration-500 ease-out-quart origin-right scale-x-100 group-hover:origin-left group-disabled:scale-x-100" />
                </span>
                <svg viewBox="0 0 24 24" fill="none" className="w-10 h-px overflow-visible text-paper transition-transform duration-500 ease-out-quart group-hover:translate-x-2" aria-hidden="true">
                  <path stroke="currentColor" strokeWidth="1" strokeLinecap="square" d="M0 12h24m0 0l-6-6m6 6l-6 6" />
                </svg>
              </button>

              {submitStatus === 'success' && (
                <p className="text-sm text-brass-soft" role="status">Message sent successfully.</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-sm text-clay" role="alert">Error sending message. Please try again.</p>
              )}
            </div>
          </form>
        </div>

        <div className="mt-20 sm:mt-28 pt-8 border-t border-paper/15 flex items-center justify-between gap-4 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-paper/50">
          <span>© {new Date().getFullYear()} {data?.trademark || 'DV Plast 2020 EOOD'}</span>
        </div>
      </div>
    </section>
  );
};

const ContactRow = ({ icon, children }) => (
  <div className="flex items-start gap-5 py-6 border-b border-paper/15">
    <span className="shrink-0 mt-1 text-paper/50" aria-hidden="true">
      {icon === 'location' && (
        <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
          <path stroke="currentColor" strokeWidth="1.3" strokeLinecap="square" d="M12 13a3 3 0 100-6 3 3 0 000 6z" />
          <path stroke="currentColor" strokeWidth="1.3" strokeLinecap="square" d="M12 22s-7-7.5-7-13a7 7 0 1114 0c0 5.5-7 13-7 13z" />
        </svg>
      )}
      {icon === 'phone' && (
        <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
          <path stroke="currentColor" strokeWidth="1.3" strokeLinecap="square" d="M4 4h4l2 5-2.5 1.5a12 12 0 006 6L15 14l5 2v4a2 2 0 01-2 2A16 16 0 012 6a2 2 0 012-2z" />
        </svg>
      )}
      {icon === 'mail' && (
        <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
          <path stroke="currentColor" strokeWidth="1.3" strokeLinecap="square" d="M3 6h18v12H3z" />
          <path stroke="currentColor" strokeWidth="1.3" strokeLinecap="square" d="M3 7l9 7 9-7" />
        </svg>
      )}
      {icon === 'social' && (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M22 12.06C22 6.5 17.5 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.93v-7.02H7.9v-2.91h2.54V9.84c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34V22c4.78-.75 8.44-4.91 8.44-9.94z" />
        </svg>
      )}
    </span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

const Field = ({ id, label, as = 'input', type = 'text', rows, value, onChange, required, autoComplete }) => {
  const common = {
    id,
    name: id,
    value,
    onChange,
    required,
    autoComplete,
    className: cn(
      'peer w-full bg-transparent border-b border-paper/30 text-paper py-3 pr-1',
      'placeholder-transparent focus:outline-none focus:border-brass-soft',
      'transition-colors duration-300 text-base'
    ),
    placeholder: label,
  };
  return (
    <div className="relative">
      {as === 'textarea' ? (
        <textarea {...common} rows={rows} />
      ) : (
        <input {...common} type={type} />
      )}
      <label
        htmlFor={id}
        className={cn(
          'absolute left-0 font-mono text-[0.68rem] uppercase tracking-[0.22em] text-paper/55',
          'transition-all duration-300 pointer-events-none',
          'top-0 peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-paper/50',
          'peer-focus:top-0 peer-focus:text-[0.68rem] peer-focus:uppercase peer-focus:tracking-[0.22em] peer-focus:text-brass-soft'
        )}
      >
        {label}
        {required && <span className="ml-1" aria-hidden="true">*</span>}
      </label>
    </div>
  );
};
